import React from 'react';
import { View, StyleSheet, Platform, ViewStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { transformFeeStructureDollarSign } from '@/utils/residents/paymentUtils';
import type { FeeStructure } from '@/types/residents/payment';
import { PaymentSectionHeader } from './PaymentSectionHeader';

type Variant = 'desktop' | 'mobile';

interface ConvenienceFeesSectionProps {
  /** Fee structure with credit, debit, and bank account fees */
  feeStructure: FeeStructure;
  /** @deprecated Use feeStructure instead */
  feeStructureDisplay?: FeeStructure;
  /** Display variant - affects styling */
  variant?: Variant;
  /** Whether to show the header */
  showHeader?: boolean;
  /** Custom container style */
  style?: ViewStyle;
}

/**
 * Unified Convenience Fees Display Component
 * Shows fee rates for Credit, Debit, and ACH payment methods
 * Supports both mobile and desktop styling variants
 */
export const ConvenienceFeesSection: React.FC<ConvenienceFeesSectionProps> = ({
  feeStructure,
  feeStructureDisplay, // deprecated - for backward compatibility
  variant = Platform.OS === 'web' ? 'desktop' : 'mobile',
  showHeader = true,
  style,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const DESKTOP_COLORS = useDesktopColors();
  // Support both prop names for backward compatibility
  const fees = feeStructure || feeStructureDisplay || {};
  const isMobile = variant === 'mobile';

  const formatFeeValue = (value?: string): string => {
    if (!value) return '-';
    return transformFeeStructureDollarSign(value);
  };

  return (
    <View style={[
      styles.container,
      isMobile ? [styles.containerMobile, { borderTopColor: MARKETPLACE_COLORS.dividerGray }] : styles.containerDesktop,
      style,
    ]}>
      {showHeader && (
        isMobile ? (
          <View style={styles.headerRowMobile}>
            <Text style={[styles.headerTextMobile, { color: MARKETPLACE_COLORS.textPrimary }]}>Convenience fees</Text>
          </View>
        ) : (
          <PaymentSectionHeader title="Convenience fees" />
        )
      )}
      
      <View style={isMobile ? styles.feesContentMobile : undefined}>
        <View style={[styles.feeRow, isMobile && styles.feeRowMobile]}>
          <Text style={isMobile ? [styles.feeLabelMobile, { color: MARKETPLACE_COLORS.textPrimary }] : [styles.feeLabelDesktop, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>Credit</Text>
          <Text style={isMobile ? [styles.feeValueMobile, { color: MARKETPLACE_COLORS.textPrimary }] : styles.feeValueDesktop}>
            {formatFeeValue(fees.creditCard)}
          </Text>
        </View>

        <View style={[styles.feeRow, isMobile && styles.feeRowMobile]}>
          <Text style={isMobile ? [styles.feeLabelMobile, { color: MARKETPLACE_COLORS.textPrimary }] : [styles.feeLabelDesktop, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>Debit</Text>
          <Text style={isMobile ? [styles.feeValueMobile, { color: MARKETPLACE_COLORS.textPrimary }] : styles.feeValueDesktop}>
            {formatFeeValue(fees.debitCard)}
          </Text>
        </View>

        <View style={[styles.feeRow, isMobile && styles.feeRowMobile]}>
          <Text style={isMobile ? [styles.feeLabelMobile, { color: MARKETPLACE_COLORS.textPrimary }] : [styles.feeLabelDesktop, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>ACH</Text>
          <Text style={isMobile ? [styles.feeValueMobile, { color: MARKETPLACE_COLORS.textPrimary }] : styles.feeValueDesktop}>
            {formatFeeValue(fees.bankAccount)}
          </Text>
        </View>
      </View>
    </View>
  );
};

// Alias for backward compatibility with mobile imports
export const ConvenienceFeesDisplay = ConvenienceFeesSection;

const styles = StyleSheet.create({
  // Container
  container: {},
  containerDesktop: {
    paddingVertical: 14,
  },
  containerMobile: {
    paddingVertical: 24,
    paddingHorizontal: 24,
    borderTopWidth: 1,
  },
  
  // Header (mobile)
  headerRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  headerTextMobile: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  
  // Fee rows
  feeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  feeRowMobile: {
    // Mobile-specific row styling
  },
  
  // Mobile content wrapper
  feesContentMobile: {
    paddingLeft: 16,
    gap: 4,
  },
  
  // Desktop labels/values
  feeLabelDesktop: {
    fontSize: 18,
    paddingVertical: 8,
  },
  feeValueDesktop: {
    color: '#10B981',
    fontSize: 18,
    fontWeight: '700',
    paddingVertical: 8,
  },
  
  // Mobile labels/values
  feeLabelMobile: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  feeValueMobile: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
});
